import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import StandardModal from 'components/shared/StandardModal';
import { ActionContext } from 'hooks/useAdminListPage';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { DenialList } from 'models/DenialList';
import { adminClientsQueryKeys, adminIndexClients } from 'services/api/admin/clients';
import { createAdminDenialList } from 'services/api/admin/denialLists';
import { BodySmall } from 'styles/typography';

type ClientOption = { id: string; name: string };

export type AddDenialListModalProps = {
  onClose: () => void;
  ctx: ActionContext;
};

// Admin "Add New List" flow: pick a client with no active denial list, create the (empty) list,
// then open the existing slideout so the admin can start adding patients. The dropdown is searchable
// + infinite-scroll and only offers inbound-referrals-enabled clients without an active list (the
// `denial_list_eligible` filter), so a successful create never collides with an existing list.
export default function AddDenialListModal({ onClose, ctx }: AddDenialListModalProps) {
  const [client, setClient] = useState<ClientOption | null>(null);
  const invalidateQueries = useInvalidateQueries();

  const clientOptions = useInfiniteOptions<ClientOption>({
    queryKey: adminClientsQueryKeys.denialListEligible,
    fetchFn: (params, signal) => adminIndexClients(params, signal),
    params: { sortBy: 'name asc', denialListEligible: true },
  });

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: createAdminDenialList,
  });

  const handleCreate = () => {
    if (!client) return;

    mutate(
      { clientId: client.id },
      {
        onSuccess: (denialList: DenialList) => {
          // The client now has a list, so drop it from the eligible-clients picker; refresh the tab.
          invalidateQueries({ denialListEligibleClients: true });
          ctx.toast(`Denial list created for ${client.name}`);
          ctx.refetch();
          onClose();
          ctx.openSlideout('items', denialList);
        },
      }
    );
  };

  return (
    <StandardModal title='Add New List' onCancel={onClose}>
      <Description>
        Select a client to start a new global denial list. You&rsquo;ll add patients to it next.
      </Description>

      <FieldContainer>
        <FieldLabel>Client</FieldLabel>
        <Select<ClientOption>
          {...clientOptions}
          value={client}
          onChange={setClient}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.name}
          placeholder='Search clients'
          disabled={isPending}
          dataTestId='client-select'
        />
      </FieldContainer>

      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleCreate} loading={isPending} disabled={!client || isPending}>
          Create List
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Description = styled(BodySmall)`
  display: block;
  margin-bottom: 24px;
`;

// Matches the field → actions gap used by the patient entry form so the dropdown isn't flush
// against the Cancel / Create List buttons.
const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
`;
